import { memo } from "react";

interface ISidebarGroupCountProps {
  count: number;
  /**
   * NOTE: NSAI-6259 follow-up — the sidebar's lists are paged, so a count of
   * what is LOADED is not a count of what exists. Set this when more pages are
   * still to come and the number renders as `12+`, which is true, instead of
   * `12`, which silently becomes `27` once the member scrolls.
   */
  isPartial?: boolean;
}

/**
 * NOTE: NSAI-6259 — the count on every collapsible group in the chat sidebar.
 * It is what makes a COLLAPSED group still say something: folding "Previous 30
 * days" away otherwise leaves a label and a chevron with no hint of how much is
 * behind them.
 *
 * It is a bare numeral, not a pill. A number in a filled rounded badge reads as
 * a notification — something unread, something to clear — and this is a fact
 * about the list, not an alert. Dropping the fill and the radius keeps the
 * information and loses the false urgency, which is also why it no longer needs
 * to hide itself: an earlier revision faded it in on hover to stop the badges
 * nagging, but with the badge styling gone there is nothing left to nag, and a
 * count you have to go hunting for is worse than one you can read at a glance.
 *
 * `text-foreground-secondary` rather than the sidebar-accent foreground it used
 * to carry: that token is tuned to sit on the accent fill this no longer has,
 * and off that fill it was too low-contrast to read in either theme.
 *
 * `ms-auto` rather than a fixed position: the trigger lays its children out
 * from the start edge, so this pushes itself to the trailing edge and the
 * accordion's own chevron settles after it, in both LTR and RTL.
 */
const SidebarGroupCountComponent = ({
  count,
  isPartial = false,
}: ISidebarGroupCountProps): JSX.Element => (
  <span
    data-testid="sidebar-group-count"
    data-partial={isPartial}
    className="ms-auto shrink-0 text-xs font-medium tabular-nums leading-[18px] text-foreground-secondary"
  >
    {isPartial ? `${count}+` : count}
  </span>
);

export const SidebarGroupCount = memo(SidebarGroupCountComponent);
